import { useEffect, useState } from 'react';
import { IPosition } from '@models/winnerSelection/ITablePositions';
import { WinnerSelectionService } from '@services/WinnerSelectionService';
import { PosInfo, mergePosInfo } from '../utils/posInfoMerge';

const CHUNK_SIZE = 100;

interface IUsePositionsOffersInfo {
    posInfo: Record<string, PosInfo>;
    /**
     * Прямой setter — нужен parent'у чтобы прокинуть результат onDataLoaded
     * из TableDemand активной позиции в общий кэш через mergePosInfo.
     */
    setPosInfo: React.Dispatch<React.SetStateAction<Record<string, PosInfo>>>;
}

/**
 * Прогрессивная подгрузка offers-info chunk'ами по 100 позиций.
 *
 * Полный fetch для 1500 позиций блокирует backend на ~60 сек. Поэтому фронт
 * сначала получает быстрый список без offers-info, затем добивает данные
 * пачками через batch /offersInfo. Каждый chunk мерджится через mergePosInfo
 * (защита от регрессии статуса).
 *
 * AbortController отменяет inflight-запросы при unmount или смене docId.
 */
export function usePositionsOffersInfo(
    positions: IPosition[] | undefined,
    docId: string,
    tableKey: string | undefined,
): IUsePositionsOffersInfo {
    const [posInfo, setPosInfo] = useState<Record<string, PosInfo>>({});

    useEffect(() => {
        if (!positions || positions.length === 0 || !tableKey) return;
        const controller = new AbortController();
        const missing = positions.filter((p) => !posInfo[p.key]).map((p) => p.key);
        if (missing.length === 0) return;

        const fetchChunks = async () => {
            for (let i = 0; i < missing.length; i += CHUNK_SIZE) {
                if (controller.signal.aborted) return;
                const chunk = missing.slice(i, i + CHUNK_SIZE);
                try {
                    const res = await WinnerSelectionService.fetchPositionsOffersInfo(
                        docId,
                        tableKey,
                        chunk,
                        controller.signal,
                    );
                    if (controller.signal.aborted) return;
                    setPosInfo((prev) => {
                        const next = { ...prev };
                        Object.entries(res.data).forEach(([key, info]) => {
                            next[key] = mergePosInfo(prev[key], info);
                        });
                        return next;
                    });
                } catch {
                    /* тихо пропускаем chunk (включая abort) */
                }
            }
        };
        fetchChunks();
        return () => {
            controller.abort();
        };
        // posInfo НЕ в deps намеренно — иначе цикл fetch'ей.
        // missing вычисляется в момент запуска эффекта; для уже-загруженных
        // позиций chunks skip'ятся в начале функции.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [positions, docId, tableKey]);

    return { posInfo, setPosInfo };
}
